import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { RefreshCcw, Plus, ChevronDown, Trash2, Pencil, Copy, MoreHorizontal, Search } from 'lucide-react'
import { getMapLabel } from './MapSelector'
import {
  editorIconButton, editorInput, editorMenu, editorMenuItem, editorMenuItemDanger,
} from './editorUi'
import { strategyEditorPath, type StrategyKind } from '@/lib/strategyKinds'
import { useLanguage } from '@/contexts/LanguageContext'

interface StrategySummary {
  id: number
  title: string
  map_name: string
  created_by_name: string
  updated_at: number
  /** Backend'in kararı — rol ve sahiplik orada birleşir (bkz. strategies/router.py). */
  can_edit: boolean
}

interface ConfigSummary {
  id: number
  title: string
  map_name: string
  created_by_name: string
  updated_at: number
  can_edit: boolean
}

function formatDate(ts: number): string {
  if (!ts) return '-'
  return new Date(ts * 1000).toLocaleString('tr-TR', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  })
}

interface ContextMenuState {
  id: number
  x: number
  y: number
  isSlide: boolean
  parentId?: number
}

interface Props {
  /**
   * Hangi bölümün stratejileri listelenecek. Kenar çubuğu editörün içinde
   * gezinme aracıdır, dolayısıyla bulunduğu bölümü **terk etmemeli**: maç
   * planı açıkken taktik defterinin satırlarını göstermek, iki farklı yetki
   * kuralını tek listede karıştırmak olurdu.
   */
  kind: StrategyKind
  activeStrategyId?: number | null
  onSelectStrategy?: (mapName: string, id: number, parentTitle?: string) => void
  onDeleteStrategy?: (id: number) => void
  onRenameStrategy?: (id: number, newTitle: string) => void
}

export default function StrategiesSidebar({
  kind,
  activeStrategyId,
  onSelectStrategy,
  onDeleteStrategy,
  onRenameStrategy,
}: Props) {
  const navigate = useNavigate()
  const { T } = useLanguage()
  const [strategies, setStrategies] = useState<StrategySummary[]>([])
  const [loading, setLoading] = useState(true)
  const [query, setQuery] = useState('')

  const [configs, setConfigs] = useState<Record<number, ConfigSummary[]>>({})
  const [loadingConfigs, setLoadingConfigs] = useState<Record<number, boolean>>({})
  const [creatingConfig, setCreatingConfig] = useState<number | null>(null)
  const [deletingId, setDeletingId] = useState<number | null>(null)

  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [activeRootId, setActiveRootId] = useState<number | null>(null)

  // Context menu
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null)
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null)

  // Rename
  const [renamingId, setRenamingId] = useState<number | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const renameInputRef = useRef<HTMLInputElement>(null)

  const fetchAll = useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetch(`/api/strategies?kind=${kind}`, { credentials: 'include' })
      if (res.ok) setStrategies(await res.json())
    } catch { /* ignore */ }
    setLoading(false)
  }, [kind])

  useEffect(() => { fetchAll() }, [fetchAll])

  // Close context menu on outside click
  useEffect(() => {
    const handleClick = () => setContextMenu(null)
    window.addEventListener('click', handleClick)
    return () => window.removeEventListener('click', handleClick)
  }, [])

  useEffect(() => {
    if (activeStrategyId == null || strategies.length === 0) return
    const isRoot = strategies.some(s => s.id === activeStrategyId)
    if (isRoot) {
      setActiveRootId(activeStrategyId)
      setExpandedId(activeStrategyId)
      fetchConfigs(activeStrategyId)
    } else {
      // Check already-loaded configs first
      for (const [pid, cfgs] of Object.entries(configs)) {
        if (cfgs.some(c => c.id === activeStrategyId)) {
          const pid_n = parseInt(pid)
          setActiveRootId(pid_n)
          setExpandedId(pid_n)
          return
        }
      }
      // Not found in loaded configs — fetch parent_id from API then expand
      fetch(`/api/strategies/${activeStrategyId}`, { credentials: 'include' })
        .then(r => r.ok ? r.json() : null)
        .then((d: { parent_id?: number } | null) => {
          if (d?.parent_id) {
            setActiveRootId(d.parent_id)
            setExpandedId(d.parent_id)
            fetchConfigs(d.parent_id)
          }
        })
        .catch(() => {})
    }
  }, [activeStrategyId, strategies]) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchConfigs = async (parentId: number) => {
    if (configs[parentId]) return
    setLoadingConfigs(prev => ({ ...prev, [parentId]: true }))
    try {
      const res = await fetch(`/api/strategies/${parentId}/configs`, { credentials: 'include' })
      if (res.ok) { const data = await res.json(); setConfigs(prev => ({ ...prev, [parentId]: data })) }
    } catch { /* ignore */ }
    setLoadingConfigs(prev => ({ ...prev, [parentId]: false }))
  }

  const refetchConfigs = async (parentId: number) => {
    setLoadingConfigs(prev => ({ ...prev, [parentId]: true }))
    try {
      const res = await fetch(`/api/strategies/${parentId}/configs`, { credentials: 'include' })
      if (res.ok) { const data = await res.json(); setConfigs(prev => ({ ...prev, [parentId]: data })) }
    } catch { /* ignore */ }
    setLoadingConfigs(prev => ({ ...prev, [parentId]: false }))
  }

  /**
   * Kök satıra tıklamak slayt listesini açar **ve stratejiyi yükler** — hangi
   * slaytın açılacağına StrategyMap karar verir (ilk slayt). Eskiden satır
   * yalnızca listeyi açıyordu, yani bir stratejiyi görmek için iki tıklama
   * gerekiyordu.
   *
   * Zaten açık stratejinin satırına tekrar tıklamak listeyi katlar ama tuvali
   * değiştirmez: katlamak bir gezinme değil, yer açma hareketidir.
   */
  const handleToggleRoot = (st: StrategySummary) => {
    if (expandedId === st.id) {
      setExpandedId(null)
    } else {
      setExpandedId(st.id)
      fetchConfigs(st.id)
    }
    if (activeRootId !== st.id) {
      setActiveRootId(st.id)
      if (onSelectStrategy) onSelectStrategy(st.map_name, st.id)
      else navigate(strategyEditorPath(kind, st.map_name, st.id))
    }
  }

  const handleSelectConfig = (c: ConfigSummary, parent: StrategySummary) => {
    setActiveRootId(parent.id)
    if (onSelectStrategy) onSelectStrategy(parent.map_name, c.id, parent.title)
    else navigate(strategyEditorPath(kind, parent.map_name, c.id))
  }

  /**
   * Sağ tık menüsü yalnızca yazılabilir satırda açılır. Yetki backend'in
   * `can_edit`'inden okunur; slayt kendi satırının değil **kökünün** yetkisini
   * taşır, çünkü bir stratejinin sahibi kök satırdır.
   */
  const openContextMenu = (
    e: React.MouseEvent, id: number, isSlide: boolean, canEdit: boolean, parentId?: number,
  ) => {
    e.preventDefault()
    e.stopPropagation()
    if (!canEdit) return
    setContextMenu({ id, x: e.clientX, y: e.clientY, isSlide, parentId })
    setConfirmDeleteId(null)
  }

  /**
   * Menünün ikinci kapısı: satırdaki "..." düğmesi. Sağ tık ile aynı menüyü
   * açar ama konumu **düğmenin altıdır**, imlecin değil — düğmeye basan kişi
   * menüyü düğmenin yanında bekler.
   */
  const openContextMenuAt = (
    e: React.MouseEvent, id: number, isSlide: boolean, canEdit: boolean, parentId?: number,
  ) => {
    e.preventDefault()
    e.stopPropagation()
    if (!canEdit) return
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    setContextMenu({ id, x: Math.min(rect.left, window.innerWidth - 180), y: rect.bottom + 4, isSlide, parentId })
    setConfirmDeleteId(null)
  }

  const startRename = (id: number, currentTitle: string) => {
    setContextMenu(null)
    setConfirmDeleteId(null)
    setRenamingId(id)
    setRenameValue(currentTitle)
    setTimeout(() => renameInputRef.current?.select(), 50)
  }

  const handleRenameSubmit = async (id: number, isSlide: boolean, parentId?: number) => {
    const trimmed = renameValue.trim()
    setRenamingId(null)
    if (!trimmed) return
    try {
      await fetch(`/api/strategies/${id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: trimmed }),
      })
      if (isSlide && parentId !== undefined) {
        setConfigs(prev => ({
          ...prev,
          [parentId]: (prev[parentId] ?? []).map(c => c.id === id ? { ...c, title: trimmed } : c),
        }))
      } else {
        setStrategies(prev => prev.map(s => s.id === id ? { ...s, title: trimmed } : s))
      }
      onRenameStrategy?.(id, trimmed)
    } catch { /* ignore */ }
  }

  const handleDeleteRoot = async (id: number) => {
    setContextMenu(null)
    setConfirmDeleteId(null)
    setDeletingId(id)
    try {
      await fetch(`/api/strategies/${id}`, { method: 'DELETE', credentials: 'include' })
      localStorage.removeItem(`strategy_${id}_ui`)
      onDeleteStrategy?.(id)
      setConfigs(prev => { const n = { ...prev }; delete n[id]; return n })
      if (expandedId === id) setExpandedId(null)
      await fetchAll()
    } catch { /* ignore */ }
    setDeletingId(null)
  }

  const handleDeleteConfig = async (id: number, parentId: number) => {
    setContextMenu(null)
    setConfirmDeleteId(null)
    setDeletingId(id)
    try {
      await fetch(`/api/strategies/${id}`, { method: 'DELETE', credentials: 'include' })
      localStorage.removeItem(`strategy_${id}_ui`)
      onDeleteStrategy?.(id)
      await refetchConfigs(parentId)
    } catch { /* ignore */ }
    setDeletingId(null)
  }

  const handleCreateConfig = async (parentId: number) => {
    setCreatingConfig(parentId)
    try {
      const res = await fetch('/api/strategies', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: T.strategy.library.slideName.replace(
            '{n}', String((configs[parentId]?.length ?? 0) + 1),
          ),
          parent_id: parentId,
        }),
      })
      if (res.ok) {
        const { id } = await res.json()
        await refetchConfigs(parentId)
        const parent = strategies.find(s => s.id === parentId)
        if (parent) {
          if (onSelectStrategy) onSelectStrategy(parent.map_name, id, parent.title)
          else navigate(strategyEditorPath(kind, parent.map_name, id))
        }
      }
    } catch { /* ignore */ }
    setCreatingConfig(null)
  }

  /**
   * Slaytı çoğaltır: kaydedilmiş tuval (çizimler, simgeler, pinli noktalar,
   * seçili bölge — hepsi `canvas_json`'da) aynen yeni bir slayta yazılır ve
   * kopya açılır. Harita ve bölüm backend'de ebeveynden miras alınır.
   *
   * Kaynak sunucudaki son kayıttır: açık slaytta otomatik kaydın henüz
   * yazmadığı son birkaç saniye kopyaya girmez.
   */
  const handleDuplicateConfig = async (id: number, parentId: number) => {
    setContextMenu(null)
    setConfirmDeleteId(null)
    const parent = strategies.find(s => s.id === parentId)
    const source = configs[parentId]?.find(c => c.id === id)
    if (!parent || !source) return
    setCreatingConfig(parentId)
    try {
      const res = await fetch(`/api/strategies/${id}`, { credentials: 'include' })
      if (!res.ok) throw new Error()
      const { canvas_json } = await res.json() as { canvas_json: string }
      const created = await fetch('/api/strategies', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: T.strategy.copyName.replace('{name}', source.title),
          parent_id: parentId,
          canvas_json,
        }),
      })
      if (created.ok) {
        const { id: newId } = await created.json()
        await refetchConfigs(parentId)
        if (onSelectStrategy) onSelectStrategy(parent.map_name, newId, parent.title)
        else navigate(strategyEditorPath(kind, parent.map_name, newId))
      }
    } catch { /* ignore */ }
    setCreatingConfig(null)
  }

  /**
   * Liste ada göre süzülür. Kenar çubuğu editörün içindeki gezinme aracıdır ve
   * bir haritanın onlarca planı olabiliyor; aranacak bir kutu olmadan tek yol
   * listeyi baştan sona kaydırmaktı.
   */
  const q = query.trim().toLocaleLowerCase('tr-TR')
  const shown = q
    ? strategies.filter(s =>
        s.title.toLocaleLowerCase('tr-TR').includes(q) ||
        getMapLabel(s.map_name).toLocaleLowerCase('tr-TR').includes(q))
    : strategies

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Başlık bandı — panelin bölüm başlığıyla aynı yüz: saç teli, display
          yüzü, geniş harf aralığı. */}
      <div className="flex h-[68px] shrink-0 items-center gap-2 border-b border-white/10 px-4">
        <span aria-hidden className="h-px w-4 shrink-0 bg-primary" />
        <h2 className="flex-1 truncate font-display text-[11px] font-bold uppercase tracking-[0.18em] text-foreground/70">
          {T.strategy.strategies}
        </h2>
        <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground/50">
          {loading ? '' : strategies.length}
        </span>
        <button
          onClick={fetchAll}
          disabled={loading}
          className={editorIconButton(false, 'h-7 w-7')}
          title={T.strategy.refresh}
        >
          <RefreshCcw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Arama */}
      <div className="shrink-0 border-b border-white/10 p-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground/50" />
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={T.strategy.library.search}
            className={`${editorInput} pl-7`}
          />
        </div>
      </div>

      {/* Liste */}
      <div className="flex-1 overflow-y-auto">
        {loading && strategies.length === 0 &&
          Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="space-y-2 border-b border-white/5 px-4 py-3">
              <div className="h-3 w-16 animate-pulse rounded-sm bg-white/8" />
              <div className="h-4 w-3/4 animate-pulse rounded-sm bg-white/8" />
              <div className="h-3 w-28 animate-pulse rounded-sm bg-white/6" />
            </div>
          ))
        }
        {!loading && strategies.length === 0 && (
          <p className="px-4 py-4 text-xs text-muted-foreground">{T.strategy.noStrategies}</p>
        )}
        {!loading && strategies.length > 0 && shown.length === 0 && (
          <p className="px-4 py-4 text-xs text-muted-foreground">{T.strategy.library.noSearchResult}</p>
        )}

        {shown.map(s => {
          const isActiveRoot = activeRootId === s.id
          const isExpanded = expandedId === s.id
          const slideList = configs[s.id] ?? []
          const isDeleting = deletingId === s.id

          return (
            <div key={s.id} className="border-b border-white/5">
              {/* Kök strateji satırı — etkin olan sol kızıl çubukla işaretlenir,
                  panelin gezinme çubuğundaki etkin satırla aynı işaret. */}
              <div
                className={`group flex cursor-pointer select-none items-start border-l-2 transition-colors hover:bg-white/5
                  ${isActiveRoot ? 'border-l-primary bg-primary/10' : 'border-l-transparent'}
                  ${isDeleting ? 'pointer-events-none opacity-40' : ''}`}
                onClick={() => { if (renamingId !== s.id) handleToggleRoot(s) }}
                onContextMenu={e => openContextMenu(e, s.id, false, s.can_edit)}
              >
                <div className="min-w-0 flex-1 px-3 pb-2 pt-3">
                  {/* Harita adı **veridir**, büyütülmez. */}
                  <span className="mb-1 inline-block rounded-sm border border-white/10 bg-white/5 px-1.5 py-0.5 font-display text-[9px] tracking-[0.06em] text-muted-foreground">
                    {getMapLabel(s.map_name)}
                  </span>
                  {renamingId === s.id ? (
                    <input
                      ref={renameInputRef}
                      value={renameValue}
                      onChange={e => setRenameValue(e.target.value)}
                      onBlur={() => handleRenameSubmit(s.id, false)}
                      onKeyDown={e => {
                        e.stopPropagation()
                        if (e.key === 'Enter') handleRenameSubmit(s.id, false)
                        if (e.key === 'Escape') setRenamingId(null)
                      }}
                      onClick={e => e.stopPropagation()}
                      className={editorInput}
                    />
                  ) : (
                    <p className="truncate font-display text-sm font-bold leading-tight tracking-[0.02em]">{s.title}</p>
                  )}
                  <div className="mt-1 flex items-center justify-between gap-2 text-[10px] text-muted-foreground/60">
                    <span className="truncate">{s.created_by_name}</span>
                    <span className="shrink-0 tabular-nums">{formatDate(s.updated_at)}</span>
                  </div>
                </div>

                {/* Satır eylemleri.
                    ⚠️ Yeniden adlandırma ve silme bir zamanlar **yalnızca sağ
                    tıkta** duruyordu: dokunmatik ekranda sağ tık yok, imleçle
                    de kimse denemeden bilmiyor. Menü artık görünür bir düğmeyle
                    de açılıyor; sağ tık olduğu gibi duruyor.

                    Düğme hover'da **belirmez**, yalnızca parlar: görünürlüğü
                    imlece bağlamak, sağ tıkla aynı kapalılıktır. */}
                <div className="flex shrink-0 items-center gap-1 pr-2 pt-3.5">
                  {s.can_edit && (
                    <button
                      onClick={e => openContextMenuAt(e, s.id, false, s.can_edit)}
                      title={T.strategy.actions}
                      className="cursor-pointer rounded-sm p-0.5 text-muted-foreground/30 transition-colors hover:bg-white/10 hover:text-foreground group-hover:text-muted-foreground"
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </button>
                  )}
                  <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground/30 transition-transform duration-200 ${isExpanded ? '' : '-rotate-90'}`} />
                </div>
              </div>

              {/* Slaytlar — açıkken */}
              {isExpanded && (
                <div className="border-t border-white/5 bg-white/[0.02] px-3 py-2">
                  <div className="mb-2 flex items-center gap-1.5">
                    <span className="font-display text-[9px] font-bold uppercase tracking-[0.18em] text-muted-foreground/50">
                      {T.strategy.slides}
                    </span>
                    {loadingConfigs[s.id] && (
                      <RefreshCcw className="h-2.5 w-2.5 animate-spin text-muted-foreground/40" />
                    )}
                  </div>

                  <div className="flex flex-col gap-1">
                    {slideList.map(c => (
                      <div key={c.id}>
                        {renamingId === c.id ? (
                          <input
                            ref={renameInputRef}
                            value={renameValue}
                            onChange={e => setRenameValue(e.target.value)}
                            onBlur={() => handleRenameSubmit(c.id, true, s.id)}
                            onKeyDown={e => {
                              e.stopPropagation()
                              if (e.key === 'Enter') handleRenameSubmit(c.id, true, s.id)
                              if (e.key === 'Escape') setRenamingId(null)
                            }}
                            onClick={e => e.stopPropagation()}
                            className={editorInput}
                          />
                        ) : (
                          <div
                            className={`group/slide flex items-center gap-1 rounded-sm border transition-colors
                              ${activeStrategyId === c.id
                                ? 'border-primary/60 bg-primary/15'
                                : 'border-white/10 bg-white/5 hover:bg-white/10'
                              }`}
                          >
                            <button
                              onClick={() => handleSelectConfig(c, s)}
                              onContextMenu={e => openContextMenu(e, c.id, true, s.can_edit, s.id)}
                              className={`min-w-0 flex-1 cursor-pointer truncate px-2 py-1.5 text-left text-[11px] font-semibold transition-colors
                                ${activeStrategyId === c.id ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                            >
                              {c.title}
                            </button>
                            {s.can_edit && (
                              <button
                                onClick={e => openContextMenuAt(e, c.id, true, s.can_edit, s.id)}
                                title={T.strategy.actions}
                                className="mr-1 shrink-0 cursor-pointer rounded-sm p-0.5 text-muted-foreground/30 transition-colors hover:bg-white/10 hover:text-foreground group-hover/slide:text-muted-foreground"
                              >
                                <MoreHorizontal className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    ))}

                    {slideList.length === 0 && !loadingConfigs[s.id] && (
                      <p className="px-1 py-1 text-[10px] text-muted-foreground/40">{T.strategy.noSlides}</p>
                    )}

                    {/* Slayt ekleme — yalnızca yazabilen görür */}
                    {s.can_edit && (
                      <button
                        onClick={() => handleCreateConfig(s.id)}
                        disabled={creatingConfig === s.id}
                        className="flex w-full cursor-pointer items-center gap-1.5 rounded-sm border border-dashed border-white/15 px-2 py-1.5 text-left text-[11px] font-semibold text-muted-foreground/60 transition-colors hover:border-primary/40 hover:text-primary disabled:opacity-40"
                      >
                        <Plus className="h-3 w-3" />
                        {creatingConfig === s.id ? T.strategy.creating : T.strategy.addSlide}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Sağ tık / "..." menüsü — kırpılmamak için portal ile body'ye çizilir */}
      {contextMenu && createPortal(
        <div
          className={`fixed z-50 min-w-[160px] py-1 ${editorMenu}`}
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onClick={e => e.stopPropagation()}
        >
          {confirmDeleteId === contextMenu.id ? (
            <div className="px-3 py-2">
              <p className="mb-2 text-[11px] text-muted-foreground">
                {contextMenu.isSlide ? T.strategy.deleteSlide : T.strategy.deleteStrategy}
              </p>
              <div className="flex gap-1.5">
                <button
                  onClick={() => contextMenu.isSlide && contextMenu.parentId !== undefined
                    ? handleDeleteConfig(contextMenu.id, contextMenu.parentId)
                    : handleDeleteRoot(contextMenu.id)
                  }
                  className="flex-1 cursor-pointer rounded-sm bg-red-500/20 px-2 py-1 text-[11px] font-semibold text-red-400 transition-colors hover:bg-red-500/30"
                >
                  {T.strategy.delete}
                </button>
                <button
                  onClick={() => { setConfirmDeleteId(null); setContextMenu(null) }}
                  className="flex-1 cursor-pointer rounded-sm bg-white/5 px-2 py-1 text-[11px] font-semibold text-muted-foreground transition-colors hover:bg-white/10"
                >
                  {T.strategy.cancel}
                </button>
              </div>
            </div>
          ) : (
            <>
              <button
                onClick={() => {
                  const item = contextMenu.isSlide
                    ? configs[contextMenu.parentId!]?.find(c => c.id === contextMenu.id)
                    : strategies.find(s => s.id === contextMenu.id)
                  if (item) startRename(contextMenu.id, item.title)
                }}
                className={editorMenuItem}
              >
                <Pencil className="h-3.5 w-3.5" />
                {T.strategy.rename}
              </button>
              {contextMenu.isSlide && contextMenu.parentId !== undefined && (
                <button
                  onClick={() => handleDuplicateConfig(contextMenu.id, contextMenu.parentId!)}
                  className={editorMenuItem}
                >
                  <Copy className="h-3.5 w-3.5" />
                  {T.strategy.duplicate}
                </button>
              )}
              <button
                onClick={() => setConfirmDeleteId(contextMenu.id)}
                className={editorMenuItemDanger}
              >
                <Trash2 className="h-3.5 w-3.5" />
                {T.strategy.delete}
              </button>
            </>
          )}
        </div>,
        document.body
      )}
    </div>
  )
}
